'use client';
import { Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useAetherContext } from '#pplx/frontend/aether/components/AetherProvider/useAetherContext.js';
import { Button } from '#pplx/frontend/aether/components/Button/index.js';
import { DropdownMenu } from '#pplx/frontend/aether/components/DropdownMenu/index.js';
import { Icon } from '#pplx/frontend/aether/components/Icon/index.js';
import { Input } from '#pplx/frontend/aether/components/Input/index.js';
import { Spinner } from '#pplx/frontend/aether/components/Spinner/index.js';
import { Tooltip } from '#pplx/frontend/aether/components/Tooltip/index.js';
import { useMenu } from '#pplx/frontend/aether/components/useMenu/index.js';
import { HomeFreePreviewLabel } from '#pplx/frontend/apps/ask/ask-composer/HomeFreePreviewLabel.js';
import { computerProjectPickerSearchChangedEvent, homepageComputerProjectSelectorClickedEvent, } from '#pplx/frontend/apps/ask/events/comet-home/analytics.js';
import { useBrandedVisitorGate } from '#pplx/frontend/apps/ask/hooks/branded-visitor-gate-hooks/useBrandedVisitorGate.js';
import { useUserPinnedSpaces } from '#pplx/frontend/apps/ask/hooks/collections-hooks/useUserPinnedSpaces.js';
import { useAtMentionSpaces } from '#pplx/frontend/apps/ask/hooks/spaces-hooks/useAtMentionSpaces.js';
import { getCollectionQueryOptions } from '#pplx/frontend/apps/ask/queries/collection-queries/getCollection.js';
import { useCreateSpaceModal } from '#pplx/frontend/apps/ask/spaces-redesign/hooks/useCreateSpaceModal.js';
import { sortPinnedFirst } from '#pplx/frontend/apps/ask/spaces-shared/sortPinnedFirst.js';
import { SpaceIcon } from '#pplx/frontend/apps/ask/spaces-shared/SpaceIcon.js';
import { useSession } from '#pplx/frontend/auth/useSession.js';
import { useDebouncedValue } from '#pplx/frontend/common/hooks/useDebouncedValue.js';
import { useReducedMotion } from '#pplx/frontend/common/hooks/useReducedMotion.js';
import { unifiedEmojiToString } from '#pplx/frontend/common/unifiedEmojiToString.js';
import { ModalOrigin } from '#pplx/frontend/components/types/types.js';
import { Chin } from '#pplx/frontend/composer-sdk/components/Chin.js';
import { ComposerDropdownCaret } from '#pplx/frontend/composer-sdk/components/ComposerDropdownCaret.js';
import { Dynamic } from '#pplx/frontend/dynamic/Dynamic.js';
import { useInstrumentation } from '#pplx/frontend/framework/instrumentation/useInstrumentation.js';
import { $icon } from '#pplx/frontend/icons/icon-sprite.js';
import { useQuery } from '#pplx/frontend/react-query/useQuery.js';
import { animated, useSpring } from '@react-spring/web';
import classNames from 'classnames';
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';
const HOME_PROJECT_MENU_WIDTH_PX = 320;
const HOME_PROJECT_MENU_MAX_HEIGHT_PX = 640;
const PROJECT_SEARCH_DEBOUNCE_MS = 200;
// Keep sticky controls above in-flow rows while allowing the overlaid scrollbar to paint above them.
const STICKY_MENU_HEADER_CLASS_NAME = 'sticky top-0 bg-raised';
const HOME_COMPUTER_CHIN_SPRING_CONFIG = {
    tension: 700,
    friction: 50,
    clamp: true,
};
const HomeComputerConnectorLauncher = Dynamic(async () => {
    const { HomeComputerConnectorLauncher } = await import('#pplx/frontend/apps/ask/ask-composer/HomeComputerConnectorLauncher.js');
    return { default: HomeComputerConnectorLauncher };
});
const HomeComputerDesktopAppPromo = Dynamic(async () => {
    const { HomeComputerDesktopAppPromo } = await import('#pplx/frontend/apps/ask/ask-composer/HomeComputerDesktopAppPromo.js');
    return { default: HomeComputerDesktopAppPromo };
});
const selectorMessages = defineMessages({
    removeFromProject: {
        defaultMessage: "Don't work in a project",
        description: 'Action to clear the selected project from a new Computer task',
        id: 'bcde2c2e89',
    },
    removeFromSpace: {
        defaultMessage: 'Remove from space',
        description: 'Menu item to remove a task from its space',
        id: '9fa8eaa875',
    },
    projectBottomSheetTitle: {
        defaultMessage: 'Work in a project',
        description: 'Homepage Computer input project selector bottom sheet title',
        id: '1ec4243684',
    },
    spaceBottomSheetTitle: {
        defaultMessage: 'Work in a space',
        description: 'Homepage Computer input Space selector bottom sheet title',
        id: 'f1ac7ddb78',
    },
    projectTriggerLabel: {
        defaultMessage: 'Work in a project',
        description: 'Homepage Computer input footer button for selecting a project',
        id: '055539c7c4',
    },
    spaceTriggerLabel: {
        defaultMessage: 'Work in a space',
        description: 'Homepage Computer input footer button for selecting a Space',
        id: '2fc3e7e2be',
    },
    noProjectsFound: {
        defaultMessage: 'No projects found',
        description: 'Empty state message when project search has no matches',
        id: 'f9cda1da96',
    },
    projectsAppearHere: {
        defaultMessage: 'Projects you can work in appear here',
        description: 'Empty state message when no projects are available',
        id: 'cfb5bd2405',
    },
    noSpacesFound: {
        defaultMessage: 'No Spaces found',
        description: 'Empty state message when no Spaces are available',
        id: '36e5a70e57',
    },
    createNewProject: {
        defaultMessage: 'Create new project',
        description: 'Homepage Computer project selector option to create a new project',
        id: 'd06ed39e4f',
    },
    createFirstProject: {
        defaultMessage: 'Create your first project',
        description: 'Homepage Computer project selector option when no projects are available',
        id: '7ae62801ca',
    },
    createProjectLoginTitle: {
        defaultMessage: 'Sign in to create Projects',
        description: 'Title for the login modal opened from the Homepage Computer project selector',
        id: '3945d963b5',
    },
    createProjectLoginDescription: {
        defaultMessage: 'Set instructions, organize sources, and collaborate with coworkers.',
        description: 'Description for the login modal opened from the Homepage Computer project selector',
        id: '2adf6239a8',
    },
    createNewSpace: {
        defaultMessage: 'Create new space',
        description: 'Homepage Computer Space selector option to create a new space',
        id: '6fdc9528f9',
    },
    searchProjects: {
        defaultMessage: 'Search projects',
        description: 'Search input label and placeholder in the Homepage Computer project selector',
        id: '1aa8534a39',
    },
    searchProjectsPlaceholder: {
        defaultMessage: 'Search projects...',
        description: 'Search input placeholder in the Homepage Computer project selector',
        id: '50966002f2',
    },
    searchSpaces: {
        defaultMessage: 'Search Spaces',
        description: 'Search input label and placeholder in the Homepage Computer Space selector',
        id: 'a82124ef18',
    },
});
const projectSelectorMessages = {
    bottomSheetTitle: selectorMessages.projectBottomSheetTitle,
    triggerLabel: selectorMessages.projectTriggerLabel,
    remove: selectorMessages.removeFromProject,
    empty: selectorMessages.noProjectsFound,
    emptyInitial: selectorMessages.projectsAppearHere,
    create: selectorMessages.createNewProject,
    createFirst: selectorMessages.createFirstProject,
    search: selectorMessages.searchProjects,
    searchPlaceholder: selectorMessages.searchProjectsPlaceholder,
};
export const HomeComputerComposerChin = React.memo(function HomeComputerComposerChin({ isVisible, animate = true, framed = false, appearance = 'composer', renderChin, showFreePreviewLabel = false, onFocusInput = noopAction, onInsertMentionNode = noopInsertMentionNode, onRemoveMentionNode = noopRemoveMentionNode, selectedProject, onSelectedProjectChange, }) {
    const reducedMotion = useReducedMotion();
    const [contentHeight, setContentHeight] = useState(0);
    const contentRef = useRef(null);
    const railStyle = useSpring({
        height: isVisible ? contentHeight : 0,
        marginTop: appearance === 'panel' && !framed
            ? `calc(var(--spacing-3) * ${isVisible ? 0 : -1})`
            : '0px',
        opacity: isVisible ? 1 : 0,
        config: HOME_COMPUTER_CHIN_SPRING_CONFIG,
        immediate: !animate || framed || reducedMotion || contentHeight === 0,
    });
    const showConnectorLauncher = appearance === 'composer';
    // Height must update before paint so the spring starts from the measured footer size
    // instead of flashing or clipping a frame after Computer mode toggles.
    useLayoutEffect(() => {
        const element = contentRef.current;
        if (!element)
            return;
        const updateContentHeight = () => {
            setContentHeight(element.scrollHeight);
        };
        updateContentHeight();
        if (typeof ResizeObserver === 'undefined') {
            return;
        }
        const observer = new ResizeObserver(updateContentHeight);
        observer.observe(element);
        return () => {
            observer.disconnect();
        };
    }, []);
    const projectSelector = (_jsx(HomeComputerProjectPicker, { framed: framed, isVisible: isVisible, showChevron: !showConnectorLauncher, selectedProject: selectedProject, onSelectedProjectChange: onSelectedProjectChange }));
    return (_jsx(animated.div, { "aria-hidden": !isVisible, inert: !isVisible || undefined, className: classNames('min-w-0 overflow-hidden', {
            'pointer-events-none': !isVisible,
        }), style: framed ? undefined : railStyle, children: _jsx("div", { ref: contentRef, children: appearance === 'panel' ? (_jsx("div", { className: classNames('flex min-w-0 items-center', framed ? 'min-h-7 px-0' : 'min-h-8 px-1.5'), children: _jsxs("div", { className: "flex min-w-0 items-center gap-1", children: [projectSelector, renderChin?.()] }) })) : (_jsx(Chin, { transparent: framed, isVisible: framed ? isVisible : undefined, animate: animate, children: _jsxs("div", { className: classNames('flex min-w-0 flex-1 items-center gap-1', renderChin !== undefined && 'flex-wrap', {
                        'py-0.5': showConnectorLauncher && !framed,
                    }), children: [projectSelector, renderChin?.(), isVisible && showConnectorLauncher && (_jsx(HomeComputerConnectorLauncher, { onFocusInput: onFocusInput, onInsertMentionNode: onInsertMentionNode, onRemoveMentionNode: onRemoveMentionNode })), showFreePreviewLabel ? (_jsxs("div", { className: "ms-auto flex shrink-0 items-center gap-2", "data-testid": "home-computer-chin-trailing", children: [isVisible && _jsx(HomeComputerDesktopAppPromo, {}), _jsx(HomeFreePreviewLabel, {})] })) : (isVisible && _jsx(HomeComputerDesktopAppPromo, {}))] }) })) }) }));
});
const noopInsertMentionNode = () => undefined;
const noopRemoveMentionNode = () => undefined;
const noopAction = () => undefined;
export function HomeComputerProjectChin({ projectSearchSource = 'homepage', isVisible, project, onProjectChange, }) {
    return (_jsx("div", { className: "flex min-w-0 items-center", children: _jsx(HomeComputerProjectPicker, { projectSearchSource: projectSearchSource, framed: false, isVisible: isVisible, showChevron: false, selectedProject: project, onSelectedProjectChange: onProjectChange }) }));
}
export function HomeComputerLaunchersChin({ isVisible, showFreePreviewLabel = false, onFocusInput = noopAction, onInsertMentionNode = noopInsertMentionNode, onRemoveMentionNode = noopRemoveMentionNode, }) {
    return (_jsxs("div", { className: classNames('flex min-w-0 flex-wrap items-center gap-1', 
        // Take the rest of the chin row so the trailing group reaches its end, as in the legacy chin.
        showFreePreviewLabel && 'flex-1'), children: [isVisible && (_jsx(HomeComputerConnectorLauncher, { onFocusInput: onFocusInput, onInsertMentionNode: onInsertMentionNode, onRemoveMentionNode: onRemoveMentionNode })), showFreePreviewLabel ? (_jsxs("div", { className: "ms-auto flex shrink-0 items-center gap-2", "data-testid": "home-computer-chin-trailing", children: [isVisible && _jsx(HomeComputerDesktopAppPromo, {}), _jsx(HomeFreePreviewLabel, {})] })) : (isVisible && _jsx(HomeComputerDesktopAppPromo, {}))] }));
}
function HomeComputerProjectPicker({ projectSearchSource = 'homepage', isVisible, framed, showChevron, selectedProject, onSelectedProjectChange, }) {
    const { track } = useInstrumentation();
    const { openCreateSpaceModal } = useCreateSpaceModal();
    const { mentionSpacesOptions, isLoading: areMentionSpacesLoading, onTrigger, } = useAtMentionSpaces({
        reason: 'home-computer-project-selector',
        isLazy: false,
        enabled: isVisible,
    });
    const { pins, isLoading: arePinsLoading } = useUserPinnedSpaces({
        reason: 'home-computer-project-selector',
        enabled: isVisible,
    });
    const sortedProjects = useMemo(() => {
        const pinnedProjects = pins.map(collectionToProjectOption);
        const pinnedUuids = pinnedProjects.map((project) => project.uuid);
        const pinnedUuidSet = new Set(pinnedUuids);
        return sortPinnedFirst([
            ...pinnedProjects,
            ...mentionSpacesOptions
                .filter((project) => !pinnedUuidSet.has(project.uuid))
                .map(mentionSpaceToProjectOption),
        ], pinnedUuids);
    }, [mentionSpacesOptions, pins]);
    const handleTrack = useCallback((action) => {
        track(homepageComputerProjectSelectorClickedEvent({
            name: 'homepage computer project selector clicked',
            data: { action },
        }));
    }, [track]);
    return (_jsx(HomeComputerProjectSelector, { projectSearchSource: projectSearchSource, framed: framed, isVisible: isVisible, showChevron: showChevron, projects: sortedProjects, isLoading: areMentionSpacesLoading || arePinsLoading, selectedProject: selectedProject, onToggle: onTrigger, onTrack: handleTrack, onSelectedProjectChange: onSelectedProjectChange, onOpenCreateSpaceModal: openCreateSpaceModal }));
}
const HomeComputerProjectSelector = React.memo(function HomeComputerProjectSelector({ projectSearchSource, framed, isVisible, showChevron, projects, isLoading, selectedProject, onToggle, onTrack, onSelectedProjectChange, onOpenCreateSpaceModal, }) {
    const { $t } = useIntl();
    const { status: sessionStatus } = useSession();
    const selectedProjectUuid = selectedProject?.targetCollection.uuid;
    const { data: selectedProjectData } = useQuery({
        ...getCollectionQueryOptions({
            collectionSlug: selectedProjectUuid,
            reason: 'home-computer-selected-project',
        }),
        enabled: isVisible && sessionStatus === 'authenticated' && Boolean(selectedProjectUuid),
        // New Tab selections outlive this cache and can be renamed in another tab.
        staleTime: 0,
    });
    const selectedProjectTitle = selectedProjectData?.collection?.uuid === selectedProjectUuid
        ? selectedProjectData?.collection?.title
        : undefined;
    const { openVisitorLoginUpsell } = useBrandedVisitorGate({
        enabled: sessionStatus === 'unauthenticated',
    });
    const [isOpen, setIsOpen] = useState(false);
    const [activeProjectUuidOnOpen, setActiveProjectUuidOnOpen] = useState(null);
    const copyMessages = projectSelectorMessages;
    useEffect(() => {
        if (!isVisible) {
            setIsOpen(false);
        }
    }, [isVisible]);
    const handleToggle = useCallback((open) => {
        if (!isVisible && open) {
            return;
        }
        setIsOpen(open);
        onTrack(open ? 'open' : 'close');
        if (open) {
            setActiveProjectUuidOnOpen(selectedProject?.targetCollection.uuid ?? null);
            onToggle();
        }
    }, [isVisible, onToggle, onTrack, selectedProject]);
    const handleProjectClear = useCallback(() => {
        onSelectedProjectChange(null);
        onTrack('clear_project');
        setIsOpen(false);
    }, [onSelectedProjectChange, onTrack]);
    const handleProjectSelect = useCallback((project) => {
        if (selectedProject?.targetCollection.uuid === project.uuid) {
            handleProjectClear();
            return;
        }
        onSelectedProjectChange(projectOptionToSelection(project));
        onTrack('select_project');
        setIsOpen(false);
    }, [handleProjectClear, onSelectedProjectChange, onTrack, selectedProject]);
    const handleCreateProject = useCallback(() => {
        onTrack('create_project');
        setIsOpen(false);
        if (sessionStatus === 'loading')
            return;
        if (sessionStatus === 'unauthenticated') {
            openVisitorLoginUpsell({
                title: $t(selectorMessages.createProjectLoginTitle),
                description: $t(selectorMessages.createProjectLoginDescription),
                origin: ModalOrigin.SPACES_LANDING,
            });
            return;
        }
        onOpenCreateSpaceModal({
            entryPoint: 'homepageProjectPicker',
            onCreated: (collection) => {
                onSelectedProjectChange(projectOptionToSelection(collectionToProjectOption(collection)));
            },
        });
    }, [
        $t,
        onOpenCreateSpaceModal,
        onSelectedProjectChange,
        onTrack,
        openVisitorLoginUpsell,
        sessionStatus,
    ]);
    const triggerLabel = selectedProjectTitle ??
        selectedProject?.targetCollection.title ??
        $t(copyMessages.triggerLabel);
    return (_jsxs("div", { className: "group/project-selector relative inline-flex min-w-0 [&:hover>[data-project-selector-trigger]>button]:bg-soft [&:hover>[data-project-selector-trigger]>button]:text-primary", children: [_jsx(DropdownMenu, { isOpen: isOpen, onToggle: handleToggle, preventOpenAutoFocus: true, minWidthPx: HOME_PROJECT_MENU_WIDTH_PX, maxWidthPx: HOME_PROJECT_MENU_WIDTH_PX, maxHeightPx: HOME_PROJECT_MENU_MAX_HEIGHT_PX, side: "bottom", align: "start", mobileBottomSheetTitle: $t(copyMessages.bottomSheetTitle), triggerElement: _jsx("div", { "data-project-selector-trigger": true, className: "inline-flex min-w-0 [&>button]:min-w-0 [&>button>span]:min-w-0", children: _jsx(Tooltip, { content: triggerLabel, side: "bottom", children: _jsx(Button, { variant: "text", size: "tiny", pill: true, expanded: isOpen, "aria-label": triggerLabel, children: _jsxs("span", { className: "flex min-w-0 items-center gap-1", children: [_jsx("span", { className: "-ms-0.5 inline-flex shrink-0", children: selectedProject ? (_jsx("span", { className: "inline-flex transition-opacity group-hover/project-selector:opacity-0 group-focus-within/project-selector:opacity-0 [@container_composer-chin_(width<480px)]:!opacity-100", children: getProjectIconAccessory(selectedProject) })) : (_jsx(Icon, { icon: $icon('folder-plus'), size: "tiny" })) }), _jsx("span", { className: "min-w-0 truncate [@container_composer-chin_(width<480px)]:hidden", children: triggerLabel }), showChevron && (_jsx("span", { className: "-me-0.5 inline-flex shrink-0 [@container_composer-chin_(width<480px)]:hidden", children: _jsx(ComposerDropdownCaret, {}) }))] }) }) }) }), children: _jsx(HomeComputerProjectMenuContent, { projectSearchSource: projectSearchSource, copyMessages: copyMessages, isOpen: isOpen, projects: projects, isLoading: isLoading, selectedProject: selectedProject, activeProjectUuidOnOpen: activeProjectUuidOnOpen, onProjectSelect: handleProjectSelect, onProjectClear: handleProjectClear, onCreateProject: handleCreateProject }) }), selectedProject && (_jsx("div", { className: classNames('pointer-events-none absolute left-0 opacity-0 transition-opacity [@container_composer-chin_(width<480px)]:hidden group-hover/project-selector:pointer-events-auto group-hover/project-selector:opacity-100 group-focus-within/project-selector:pointer-events-auto group-focus-within/project-selector:opacity-100 [&>button:hover]:!bg-transparent', 
                // Inline baseline overflow otherwise inflates the measured rail height.
                framed ? 'inset-y-0 flex items-center' : 'top-0'), children: _jsx(Button, { variant: "text", size: "tiny", icon: $icon('x'), rounded: true, "aria-label": $t(copyMessages.remove), onClick: handleProjectClear }) }))] }));
});
const HomeComputerProjectMenuContent = React.memo(function HomeComputerProjectMenuContent({ projectSearchSource = 'homepage', copyMessages, isOpen, projects, isLoading, selectedProject, activeProjectUuidOnOpen, onProjectSelect, onProjectClear, onCreateProject, }) {
    const { $t } = useIntl();
    const { isMobileStyle } = useAetherContext();
    const { track } = useInstrumentation();
    const menu = useMenu();
    const projectListRef = useRef(null);
    const [searchQuery, setSearchQuery] = useState('');
    const hasProjects = projects.length > 0;
    const normalizedSearchQuery = searchQuery.trim();
    const hasSearchQuery = Boolean(normalizedSearchQuery);
    const debouncedSearchQuery = useDebouncedValue(normalizedSearchQuery, PROJECT_SEARCH_DEBOUNCE_MS);
    const { data: projectSearchData, isFetching: isProjectSearchFetching, isError: isProjectSearchError, isPaused: isProjectSearchPaused, } = useQuery({
        queryKey: ['/rest/spaces/search', debouncedSearchQuery],
        queryFn: async () => (await import('#pplx/frontend/apps/ask/queries/spaces-queries/searchProjects.js')).default(debouncedSearchQuery),
        enabled: isOpen && Boolean(debouncedSearchQuery),
        staleTime: 0,
        gcTime: 0,
        meta: { skipPersistence: true },
    });
    const isResultLoading = isLoading ||
        (hasSearchQuery && (normalizedSearchQuery !== debouncedSearchQuery || isProjectSearchFetching));
    const projectsInOpenOrder = useMemo(() => {
        if (activeProjectUuidOnOpen == null) {
            return projects;
        }
        const activeProjectIndex = projects.findIndex(({ uuid }) => uuid === activeProjectUuidOnOpen);
        if (activeProjectIndex <= 0) {
            return projects;
        }
        return [
            projects[activeProjectIndex],
            ...projects.slice(0, activeProjectIndex),
            ...projects.slice(activeProjectIndex + 1),
        ];
    }, [activeProjectUuidOnOpen, projects]);
    useEffect(() => {
        if (isOpen) {
            setSearchQuery('');
        }
    }, [isOpen]);
    const displayedProjects = hasSearchQuery
        ? isProjectSearchError || isProjectSearchPaused
            ? projectsInOpenOrder.filter((project) => project.label.toLowerCase().includes(normalizedSearchQuery.toLowerCase()))
            : (projectSearchData?.results ?? []).map(collectionToProjectOption)
        : projectsInOpenOrder;
    const handleSearchQueryChange = useCallback((nextSearchQuery) => {
        const hadSearchQuery = Boolean(searchQuery.trim());
        const hasSearchQuery = Boolean(nextSearchQuery.trim());
        if (hadSearchQuery !== hasSearchQuery) {
            track(computerProjectPickerSearchChangedEvent({
                name: 'computer project picker search changed',
                data: {
                    source: projectSearchSource,
                    action: hasSearchQuery ? 'started' : 'cleared',
                },
            }));
        }
        setSearchQuery(nextSearchQuery);
    }, [projectSearchSource, searchQuery, track]);
    const focusFirstProjectItem = useCallback(() => {
        const firstProjectItem = projectListRef.current?.querySelector('[role="menuitemradio"], [role="radio"]');
        if (firstProjectItem) {
            firstProjectItem.focus();
            return;
        }
        menu?.focusFirstMenuItem();
    }, [menu]);
    const handleSearchKeyDown = useCallback((event) => {
        if (event.key === 'ArrowDown') {
            // Suppress the input's caret movement so focus can move to the first project result.
            event.preventDefault();
            focusFirstProjectItem();
            return;
        }
        if (event.key === 'Enter') {
            // Suppress Radix's default menu handling so the first search result is selected.
            event.preventDefault();
            if (isResultLoading) {
                return;
            }
            const firstProject = displayedProjects.at(0);
            if (firstProject) {
                onProjectSelect(firstProject);
            }
            return;
        }
        if (event.key === 'Escape') {
            return;
        }
        if (event.key.length === 1 ||
            event.key === 'ArrowLeft' ||
            event.key === 'ArrowRight' ||
            event.key === 'ArrowUp' ||
            event.key === 'Backspace' ||
            event.key === 'Delete' ||
            event.key === 'Home' ||
            event.key === 'End') {
            // Keep menu typeahead and navigation from intercepting text-editing keys.
            event.stopPropagation();
        }
    }, [displayedProjects, focusFirstProjectItem, isResultLoading, onProjectSelect]);
    const handleProjectValueChange = useCallback((projectUuid) => {
        const project = displayedProjects.find(({ uuid }) => uuid === projectUuid);
        if (project) {
            onProjectSelect(project);
        }
    }, [displayedProjects, onProjectSelect]);
    return (_jsxs(_Fragment, { children: [hasProjects && (_jsxs("div", { className: STICKY_MENU_HEADER_CLASS_NAME, children: [_jsx("div", { className: "pb-1", children: _jsx(Input, { type: "search", spellCheck: false, size: "small", autoFocus: !isMobileStyle, "aria-label": $t(copyMessages.search), placeholder: $t(copyMessages.searchPlaceholder), value: searchQuery, onValueChange: handleSearchQueryChange, onKeyDown: handleSearchKeyDown, onClear: hasSearchQuery ? () => handleSearchQueryChange('') : undefined, className: "!border-0 !bg-transparent" }) }), _jsx("div", { role: "separator", "aria-orientation": "horizontal", className: "mx-2 border-t border-soft" })] })), isResultLoading ? (_jsx(DropdownMenu.InfoItem, { children: _jsx(Spinner, { size: 16, color: "quiet" }) })) : !hasProjects ? (_jsx(DropdownMenu.InfoItem, { children: _jsx("span", { className: "text-secondary", children: _jsx(FormattedMessage, { ...copyMessages.emptyInitial }) }) })) : displayedProjects.length === 0 ? (_jsx("div", { className: "pt-1", children: _jsx(DropdownMenu.InfoItem, { children: _jsx("span", { className: "text-secondary", children: _jsx(FormattedMessage, { ...copyMessages.empty }) }) }) })) : (_jsx("div", { ref: projectListRef, role: "presentation", className: "pt-1", children: _jsx(DropdownMenu.RadioGroup, { value: selectedProject?.targetCollection.uuid ?? '', onValueChange: handleProjectValueChange, children: displayedProjects.map((project) => (_jsx(DropdownMenu.RadioItem, { value: project.uuid, leadingAccessory: getProjectIconAccessory(project), textValue: project.label, children: project.label }, project.uuid))) }) })), _jsxs("div", { className: "bg-raised sticky bottom-0", children: [_jsx("div", { role: "separator", "aria-orientation": "horizontal", className: "mx-2 my-1 border-t border-soft" }), selectedProject && (_jsx(DropdownMenu.Item, { leadingAccessory: $icon('x'), onSelect: onProjectClear, children: _jsx(FormattedMessage, { ...copyMessages.remove }) })), _jsx(DropdownMenu.Item, { leadingAccessory: $icon('plus'), onSelect: onCreateProject, children: _jsx(FormattedMessage, { ...(isLoading || hasProjects ? copyMessages.create : copyMessages.createFirst) }) })] })] }));
});
function collectionToProjectOption(collection) {
    const unifiedEmoji = collection.appearance?.emoji ?? collection.emoji;
    const emoji = unifiedEmoji ? unifiedEmojiToString(unifiedEmoji) : null;
    return {
        uuid: collection.uuid,
        variant: 'space',
        label: collection.title,
        icon: emoji ? { type: 'emoji', char: emoji } : { type: 'icon', icon: $icon('folder') },
        emojiCharacter: emoji ?? undefined,
        targetCollection: {
            uuid: collection.uuid,
            slug: collection.slug,
            title: collection.title,
            emoji: collection.emoji ?? undefined,
            access: collection.access,
            user_permission: collection.user_permission,
            appearance: collection.appearance,
        },
    };
}
function mentionSpaceToProjectOption(project) {
    return {
        ...project,
        emojiCharacter: project.icon?.type === 'emoji' ? project.icon.char : undefined,
        targetCollection: {
            uuid: project.uuid,
            title: project.label,
        },
    };
}
function projectOptionToSelection(project) {
    return {
        targetCollection: project.targetCollection,
        emojiCharacter: project.emojiCharacter,
    };
}
function getProjectIconAccessory(project) {
    const appearance = project.targetCollection.appearance;
    if (appearance?.icon != null || appearance?.emoji != null) {
        return _jsx(SpaceIcon, { space: { appearance }, size: "small" });
    }
    if (project.emojiCharacter) {
        return _jsx("span", { "aria-hidden": true, children: project.emojiCharacter });
    }
    const icon = 'icon' in project ? project.icon : undefined;
    if (icon?.type === 'emoji') {
        return _jsx("span", { "aria-hidden": true, children: icon.char });
    }
    if (icon?.type === 'icon') {
        return _jsx(Icon, { icon: icon.icon, size: "small" });
    }
    if (icon?.type === 'image') {
        return _jsx("img", { alt: "", className: "size-4 rounded-xs object-cover", src: icon.url });
    }
    return _jsx(Icon, { icon: $icon('folder'), size: "small" });
}
//# sourceMappingURL=HomeComputerComposerChin.js.map